import { Select } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { useOrg } from '../api/org';
import { listProjects } from '../api/gen';

/**
 * The project a page is scoped to, kept in the URL as `?project=` so a link
 * to a project's environments or knowledge is a link rather than a click path.
 * With nothing chosen the first project is, because environments and stores
 * belong to a project and a page of them has to be about one.
 */
export function useProjectParam(): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const project = params.get('project') ?? '';
  const set = (id: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // What was chosen inside the old project does not exist in the new one.
        if (prev.get('project') && prev.get('project') !== id) {
          next.delete('env');
          next.delete('store');
        }
        next.set('project', id);
        return next;
      },
      { replace: true },
    );
  return [project, set];
}

export function ProjectSelect({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const headers = useOrg();
  const { data } = useQuery({
    queryKey: ['projects'],
    queryFn: () => unwrap(listProjects({ headers, query: { limit: 100 } })),
  });
  const projects = data?.data ?? [];

  useEffect(() => {
    if (!value && projects.length > 0) onChange(projects[0]!.id);
  }, [value, projects, onChange]);

  return (
    <Select
      placeholder={projects.length === 0 ? 'No projects' : 'Project'}
      data={projects.map((p) => ({ value: p.id, label: p.slug }))}
      value={value || null}
      onChange={(v) => v && onChange(v)}
      allowDeselect={false}
      searchable
      w={220}
    />
  );
}
